import type {
  AnalyticsEventMap,
  AnalyticsEventName,
  SanitizedAnalyticsCapture,
} from "./events";
import { sanitizeAnalyticsCapture } from "./events";
import type { AnalyticsClient, AnalyticsTransport } from "./analytics-client";

const MAX_QUEUE_SIZE = 20;

export const createQueuedAnalyticsClient = (): AnalyticsClient => {
  let enabled = true;
  let transport: AnalyticsTransport | null = null;
  const queuedCaptures: SanitizedAnalyticsCapture[] = [];

  const send = (capture: SanitizedAnalyticsCapture): void => {
    try {
      transport?.capture(capture);
    } catch {
      // Analytics must never interrupt product behavior.
    }
  };

  return {
    capture: <K extends AnalyticsEventName>(
      event: K,
      properties: AnalyticsEventMap[K],
    ): void => {
      if (!enabled) return;

      const capture = sanitizeAnalyticsCapture(event, properties);
      if (capture === null) return;

      if (transport !== null) {
        send(capture);
        return;
      }

      if (queuedCaptures.length === MAX_QUEUE_SIZE) queuedCaptures.shift();
      queuedCaptures.push(capture);
    },
    attachTransport: (nextTransport: AnalyticsTransport): void => {
      if (!enabled) return;

      transport = nextTransport;
      const capturesToFlush = queuedCaptures.splice(0);
      for (const capture of capturesToFlush) send(capture);
    },
    disable: (): void => {
      enabled = false;
      queuedCaptures.splice(0);
      transport = null;
    },
  };
};
